import { Plus, Minus } from "lucide-react";
import { IMaskInput } from "react-imask";
import { useController } from "react-hook-form";
import type { Lens } from "@hookform/lenses";

export type QuantityInputProps = {
  id: string;
  lens: Lens<number>;
  min?: number;
  max?: number;
  className?: string;
  buttonClassName?: string;
  inputClassName?: string;
  onChange?: (value: number) => void; // Optional callback after lens update
};

export function QuantityInput(props: QuantityInputProps) {
  const {
    lens,
    min = 0,
    max = 9999,
    onChange,
    className = "h-10 flex items-center justify-between rounded border border-gray-300 bg-white",
    buttonClassName = "p-3 text-gray-400 hover:text-gray-600",
    inputClassName = "w-12 border-0 text-center text-base focus:outline-none"
  } = props;

  // Use lens controller
  const { field } = useController(lens.interop());

  // Get current value from lens
  const value = field.value || 0;

  // Handle value updates - update lens and call onChange callback
  const handleValueChange = (newValue: number) => {
    field.onChange(newValue);
    onChange?.(newValue); // Call optional onChange callback after lens update
  };

  const handleDecrease = () => {
    const newValue = Math.max(min, (value || 0) - 1);
    handleValueChange(newValue);
  };

  const handleIncrease = () => {
    const newValue = Math.min(max, (value || 0) + 1);
    handleValueChange(newValue);
  };

  const handleInputChange = (inputValue: string) => {
    const newValue = parseInt(inputValue, 10);
    if (!Number.isNaN(newValue)) {
      handleValueChange(Math.max(min, Math.min(max, newValue)));
    }
  };
  const currentValue = value.toString();

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handleDecrease}
        className={buttonClassName}
        aria-label="Decrease quantity"
        data-testid="quantity-decrease"
      >
        <Minus className="h-4 w-4" />
      </button>

      <IMaskInput
        mask={Number}
        scale={0}
        lazy={false}
        unmask={true}
        value={currentValue}
        onAccept={handleInputChange}
        onBlur={field.onBlur}
        className={inputClassName}
        min={min}
        max={max}
        placeholder="0"
        data-testid="quantity-input"
      />

      <button
        type="button"
        onClick={handleIncrease}
        className={buttonClassName}
        aria-label="Increase quantity"
        data-testid="quantity-increase"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
